import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { Button, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconLayoutDashboard, IconRefresh } from '@tabler/icons-react';
import { ErrorPageLayout } from '@/components/layout/ErrorPageLayout';
import { NotFoundPage } from './NotFoundPage';

/**
 * Router errorElement. Replaces React Router's default "Unexpected Application
 * Error!" screen for anything a route throws while rendering.
 */
export function RouteErrorPage(): JSX.Element {
  const error = useRouteError();

  useEffect(() => {
    console.error('Route error:', error);
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <ErrorPageLayout
      hero={
        <ThemeIcon size={72} radius={1000} variant="light" color="coral">
          <IconAlertTriangle size={36} stroke={1.5} />
        </ThemeIcon>
      }
      title="Something went wrong"
      description="This page hit an unexpected error. Reloading usually sorts it out."
      actions={
        <>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => window.location.reload()}>
            Reload page
          </Button>
          {/* Full page load rather than a router Link, so app state is reset */}
          <Button
            component="a"
            href="/"
            variant="outline"
            leftSection={<IconLayoutDashboard size={16} />}
          >
            Go to dashboard
          </Button>
        </>
      }
    />
  );
}
